import { getWorkbookDeletionDeadline } from 'cadenza/api-client/deletion-deadline-api';
import { isFeatureAvailable } from 'cadenza/features';
import { getNavigatorTree } from 'cadenza/navigator/navigator-tree-api';
import type { NavigatorTreeNodeDto } from 'cadenza/repository/model/navigator-tree-node-dto';
import { WorkbookId } from 'cadenza/repository/model/workbook-id.gen';
import type { WorkbookName } from 'cadenza/repository/model/workbook-name.gen';
import { repositoryService } from 'cadenza/repository/repository-service';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import { getStore } from 'cadenza/store/cadenza-store';
import { getProjectId } from 'cadenza/store/cadenza.state';
import type { ManageWorkbookDialogTab } from 'cadenza/workbook/manage-workbook-dialog/manage-workbook-dialog';
import { getWorkbookLabelsAndPath } from 'cadenza/workbook/manage-workbook-dialog/workbook-navigator-util';
import {
  getWorkbookNameAndHash,
  getWorkbookPrintName,
  getWorkbookRepositoryName,
  isWorkbookDeletionDeadlineEnabled,
  isWorkbookSaved,
  isWorkbookVisible,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { type WorkbookState } from 'cadenza/workbook/workbook.state';

/**
 * Returns whether the user is allowed to open the workbook management dialog.
 *
 * - The workbook management dialog can be opened if the "workbook-management" feature is available.
 * - The feature has a dependency on "workbook-design" defined, which can be disabled in the simplified operation mode.
 * - Users with at least the "visible" privilege on the workbook should be able to open the dialog even when the feature
 *   is not available to not force them to repeatedly disable and enable the simplified operation mode while configuring
 *   that mode in the dialog.
 */
export function canManageWorkbook(state: RootStateOr<WorkbookState>) {
  return isWorkbookFeatureAvailable(state, 'workbook-management') || isWorkbookVisible(state);
}

export function openManageWorkbookDialog(initiallyOpenTab?: ManageWorkbookDialogTab) {
  const repositoryName = getWorkbookRepositoryName(getStore().getState());
  const workbookName = getWorkbookNameAndHash(getStore().getState())!.id;
  const workbookId = WorkbookId.of(repositoryName, workbookName);
  const workbookPrintName = getWorkbookPrintName(getStore().getState());
  const workbookSavedInProject = !!getProjectId(getStore().getState());

  return Promise.all([
    repositoryService().get(repositoryName),
    import('cadenza/workbook/manage-workbook-dialog/manage-workbook-dialog'),
    getNavigatorTree(),
    isFeatureAvailable('DELETION_DEADLINE') && isWorkbookDeletionDeadlineEnabled(getStore().getState()) ?
      getWorkbookDeletionDeadline(workbookId)
    : undefined,
  ]).then(([databaseRepository, { ManageWorkbookDialog }, navigatorTree, deletionDeadlineInfo]) => {
    const { labels, folders, repositoryPrintName } = getManageWorkbookParameters(
      databaseRepository.printName,
      navigatorTree,
      workbookName,
    );
    return new ManageWorkbookDialog({
      workbookId,
      workbookPrintName,
      repositoryPrintName,
      folders,
      labels,
      initiallyOpenTab,
      accessTabHidden: workbookSavedInProject,
      deletionDeadlineInfo,
    }).connectAndShow();
  });
}

function getManageWorkbookParameters(
  repositoryPrintName: string,
  navigatorTree: NavigatorTreeNodeDto,
  workbookName: WorkbookName,
) {
  if (isWorkbookSaved(getStore().getState())) {
    return { repositoryPrintName, ...getWorkbookLabelsAndPath(navigatorTree, workbookName) };
  } else {
    return { repositoryPrintName: `${repositoryPrintName}*`, labels: [], folders: [] };
  }
}
